import { AppBar, Box, Button, Stack, Toolbar, Typography } from "@mui/material";

import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router-dom";

import { ThemeSwitch } from "@modules/administration/components/ThemeSwitch";
import { useLazyListTenantsTenantsGetQuery } from "api";
import { TextInput } from "components/forms";
import { WIPMessage } from "components/WIPMessage/WIPMessage";
import { useNavigate } from "hooks/useNavigate";
import * as LOCAL_STORAGE from "utils/localStorage";

import { APISelector } from "../components/APISelector";

export function LoginScreen() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { search } = useLocation();
  const redirectreason = new URLSearchParams(search).get("redirectreason");
  const defaultApiEndpoint = LOCAL_STORAGE.getCustomAPIEndPoint() || "";
  const defaultAuthBackend = LOCAL_STORAGE.getCustomAuthBackend() || "";

  // last email
  const lastEmail: string | null = localStorage.getItem("LAST_USED_EMAIL");

  const [getTenants, { data: tenantsData }] =
    useLazyListTenantsTenantsGetQuery();

  const {
    handleSubmit,
    control,
    watch,
    formState: { errors },
  } = useForm<{ email: string }>({
    defaultValues: {
      email: lastEmail ?? "",
    },
  });

  const onSubmit = async ({ email }: { email: string }) => {
    const { data: tenants } = await getTenants({ email });

    if (tenants?.data) {
      if (tenants.data.length > 0) {
        navigate(`/auth/tenants`, {
          state: { tenants: tenants?.data, email: email },
        });
      }
    }
  };

  const onUpdateAPIEndPoint = (endpoint: string) => {
    LOCAL_STORAGE.setCustomAPIEndPoint(endpoint);
  };

  const onUpdateAuthBackend = (backend: string) => {
    LOCAL_STORAGE.setCustomAuthBackend(backend);
  };

  const emailWatch = watch("email");

  return (
    <Box>
      <AppBar
        position="fixed"
        sx={(theme) => ({
          boxShadow: "none",
          background: theme.vars.palette.background.paper,
          color: theme.vars.palette.text.primary,
        })}
      >
        <Toolbar sx={{ justifyContent: "space-between", gap: 1 }}>
          <Box sx={{ height: 64, padding: 1.5, flexGrow: 1 }}>
            <img
              src={"/images/vitalthings-logo.png"}
              height={"100%"}
              alt="VitalThings Logo"
            />
          </Box>

          <ThemeSwitch />
        </Toolbar>
      </AppBar>

      {/* Warning message */}
      <WIPMessage messageKey={"login"} />

      <Box
        component={"form"}
        onSubmit={handleSubmit(onSubmit)}
        display={"flex"}
        justifyContent="center"
      >
        <Stack
          spacing={4}
          alignItems="center"
          justifyContent="center"
          padding={1}
          sx={{ minHeight: "100vh", width: "400px" }}
        >
          {tenantsData?.data?.length === 0 && (
            <Typography color={"error"}>
              {t("no_tenants_for_this_email")}
            </Typography>
          )}
          {redirectreason === "unauthorize" && (
            <Typography color={"error"}>
              {t("your_session_has_expired_please_login_again_to_continue")}
            </Typography>
          )}
          <Typography variant={"headingLargeRegular"}>
            {t("your_email_address")}
          </Typography>
          <TextInput
            label={t("Email")}
            fieldName={"email"}
            control={control}
            placeholder={t("email_id_placeholder")}
            required
            hideLabel
          />
          {import.meta.env.VITE_AUTH_BACKENDS &&
            import.meta.env.VITE_API_END_POINTS && (
              <>
                <APISelector
                  defaultApi={defaultAuthBackend}
                  urls={import.meta.env.VITE_AUTH_BACKENDS.split(",")}
                  label="Auth Backend"
                  onUpdate={onUpdateAuthBackend}
                />
                <APISelector
                  defaultApi={defaultApiEndpoint}
                  urls={import.meta.env.VITE_API_END_POINTS.split(",")}
                  label="API Endpoint"
                  onUpdate={onUpdateAPIEndPoint}
                />
              </>
            )}
          <Button disabled={!!errors.email || !emailWatch} type={"submit"}>
            {t("Continue")}
          </Button>
        </Stack>
      </Box>
    </Box>
  );
}
